import type { ComponentProps } from 'react'
import { useCallback, useMemo, useState } from 'react'

import classes from './LiveAnnouncer.module.scss'
import type { LiveAnnouncerContextValue } from './LiveAnnouncerContext'
import { LiveAnnouncerContext } from './LiveAnnouncerContext'

type LiveAnnouncerState = {
  message: string
  /**
   * If `announce` gets called with the same message more than once in a row,
   * the screen reader won't announce it, as nothing has changed in the DOM.
   * repeatCount makes it so that an additional empty space is added to the message
   * for every time the same message is announced in a row, which triggers a re-announcement.
   */
  repeatCount: number
}

const ZERO_WIDTH_SPACE = '\u200B'
/**
 * If for some reason announce gets called in an infinite loop,
 * we don't want the announcement to be repeated infinitely.
 */
const MAX_ANNOUNCEMENT_REPEAT_COUNT = 5

const AnnouncementRegion = ({
  mode,
  message,
  repeatCount = 0
}: LiveAnnouncerState & { mode: 'assertive' | 'polite' }) => (
  <div aria-atomic="true" aria-live={mode} className={classes.srOnly}>
    {`${message}${ZERO_WIDTH_SPACE.repeat(Math.min(repeatCount, MAX_ANNOUNCEMENT_REPEAT_COUNT - 1))}`}
  </div>
)

/**
 * Provider component. Mount once.
 */
export const LiveAnnouncer = ({
  children
}: Omit<ComponentProps<typeof LiveAnnouncerContext.Provider>, 'value'>) => {
  const [assertiveState, setAssertiveState] = useState<LiveAnnouncerState>({
    message: '',
    repeatCount: 0
  })
  const [politeState, setPoliteState] = useState<LiveAnnouncerState>({
    message: '',
    repeatCount: 0
  })

  const announce = useCallback<LiveAnnouncerContextValue['announce']>(
    (message, assertive = false) =>
      /* Defer to next frame to avoid swallowing during rapid DOM updates */
      window.queueMicrotask(() => {
        if (assertive) {
          setAssertiveState((currentState) => ({
            message,
            repeatCount:
              currentState.message === message
                ? currentState.repeatCount + 1
                : 0
          }))
        } else {
          setPoliteState((currentState) => ({
            message,
            repeatCount:
              currentState.message === message
                ? currentState.repeatCount + 1
                : 0
          }))
        }
      }),
    []
  )

  const context = useMemo<LiveAnnouncerContextValue>(
    () => ({ announce }),
    [announce]
  )

  return (
    <LiveAnnouncerContext.Provider value={context}>
      {/**
       * Rendering one region for each mode is necessary to avoid
       * the screen reader from swallowing the a polite announcement
       * if an assertive announcement is made concurrently
       */}
      <AnnouncementRegion
        message={assertiveState.message}
        mode="assertive"
        repeatCount={assertiveState.repeatCount}
      />
      <AnnouncementRegion
        message={politeState.message}
        mode="polite"
        repeatCount={politeState.repeatCount}
      />
      {children}
    </LiveAnnouncerContext.Provider>
  )
}
